import {useCallback, useEffect, useState} from 'react';
import type {AiChatPreferences} from '@Components/ai-chat-window/lib/ai-chat-preferences.library';
import {hideActionTiles, setAiChatGenerateAudio as setAiChatGenerateAudioApi} from '@Components/ai-chat-window/lib/ai-chat-preferences.library';
import {DEFAULT_PREFERENCES, getAiChatPreferences, hasUserSeenAiChatTemplatesTile, markAiChatTemplatesTileAsSeen} from '@Components/ai-chat-window/lib/ai-chat-preferences.library';

interface UseAiChatPreferencesReturn {
  preferences: AiChatPreferences;
  hasSeenTemplatesTile: boolean;
  hideActionTilesPermanently: () => void;
  setGenerateAudio: (generateAudio: boolean) => void;
  markTemplatesTileAsSeen: () => void;
}

export function useAiChatPreferences(isLoggedIn: boolean): UseAiChatPreferencesReturn {
  const [preferences, setPreferences] = useState<AiChatPreferences>(DEFAULT_PREFERENCES);
  const [hasSeenTemplatesTile, setHasSeenTemplatesTile] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setPreferences(DEFAULT_PREFERENCES);
      setHasSeenTemplatesTile(false);
      return;
    }

    let isCurrent = true;

    void getAiChatPreferences().then((result) => {
      if (isCurrent) {
        setPreferences(result);
      }
    });

    void hasUserSeenAiChatTemplatesTile().then((result) => {
      if (isCurrent) {
        setHasSeenTemplatesTile(result);
      }
    });

    return (): void => {
      isCurrent = false;
    };
  }, [isLoggedIn]);

  const hideActionTilesPermanently = useCallback((): void => {
    setPreferences((prev) => ({...prev, areActionTilesHidden: true}));
    void hideActionTiles();
  }, []);

  const setGenerateAudio = useCallback((generateAudio: boolean): void => {
    setPreferences((prev) => ({...prev, generateAudio}));
    void setAiChatGenerateAudioApi(generateAudio);
  }, []);

  const markTemplatesTileAsSeen = useCallback((): void => {
    setHasSeenTemplatesTile((prev) => {
      if (prev) {
        return prev;
      }
      void markAiChatTemplatesTileAsSeen().catch((e: unknown) => {
        console.error('Failed to mark templates tile as seen:', e);
      });
      return true;
    });
  }, []);

  return {
    preferences,
    hasSeenTemplatesTile,
    hideActionTilesPermanently,
    setGenerateAudio,
    markTemplatesTileAsSeen,
  };
}
